import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { PdfDocumentDetailsRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/pdf/pdf-document-details-renderer';
import type { CdaDocument, CdaSection } from '@ctw/shared/utils/cda/types';
import { Link, StyleSheet, Text, View } from '@react-pdf/renderer';
import { memo } from 'react';
import { globalStyles } from './pdf-global-styles';
import { headingStyles } from './pdf-heading-renderer';

interface PdfTableOfContentsRendererProps {
  patient: PatientModel;
  cdaDocument: CdaDocument;
  cdaDocumentSectionIdAllowlist?: Array<string>;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    width: '100%',
    marginBottom: 10,
    gap: 5,
  },
  listItem: {
    fontWeight: 400,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  listItemTextContainer: { flex: 1, display: 'flex', flexDirection: 'row', gap: 2 },
  bullet: {
    fontWeight: 400,
    marginLeft: 5,
    marginRight: 5,
  },
});

export const PdfTableOfContentsRenderer = memo(
  ({ patient, cdaDocument, cdaDocumentSectionIdAllowlist }: PdfTableOfContentsRendererProps) => {
    return (
      <View style={styles.container}>
        <Text style={headingStyles.h2}>Table of Contents</Text>
        {cdaDocument.content?.sections.map((cdaSection, cdaSectionIndex) => (
          <PdfTableOfContentsItemRenderer
            key={`toc-${cdaSection.id}-${cdaSectionIndex}`}
            cdaSection={cdaSection}
            cdaSectionIndex={cdaSectionIndex}
            presentInPdf={
              cdaDocumentSectionIdAllowlist === undefined ||
              cdaDocumentSectionIdAllowlist.includes(cdaSection.id)
            }
          />
        ))}
        <PdfDocumentDetailsRenderer patient={patient} cdaDocument={cdaDocument} />
      </View>
    );
  },
);

interface PdfTableOfContentsItemRendererProps {
  cdaSection: CdaSection;
  cdaSectionIndex: number;
  presentInPdf: boolean;
}

const PdfTableOfContentsItemRenderer = ({
  cdaSection,
  cdaSectionIndex,
  presentInPdf,
}: PdfTableOfContentsItemRendererProps) => {
  return (
    <View style={styles.listItem}>
      <Text style={[globalStyles.text, styles.bullet]}>{cdaSectionIndex + 1}.</Text>
      <View style={styles.listItemTextContainer}>
        {presentInPdf ? (
          <Link
            style={[globalStyles.text, globalStyles.link]}
            src={`#section-${cdaSection.id}-${cdaSection.index}`}
          >
            {cdaSection.title}
          </Link>
        ) : (
          <Text style={globalStyles.text}>{cdaSection.title}</Text>
        )}
        {!presentInPdf && <Text style={globalStyles.text}>(omitted)</Text>}
      </View>
    </View>
  );
};
